import { Booking } from "@collins/react-componments/dist/types/booking";
import {
  ViewBooking,
  ViewBookingAssignedAreasInner,
  ViewBookingStatusEnum
} from "collins-js-sdk";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { DiaryContextType } from "../context/DiaryContext";
import { BookingStagesEnum } from "../types/bookingEnums";
import { BookingV4 } from "../types/bookings";
import { VenueGroupConfig } from "../types/VenueGroup";
import {
  Areas,
  BookableArea,
  BookableAreaCombination,
  BookingStage,
  BookingType,
  BookingZone,
  OpeningTime,
  OpeningTimes,
  VenueConfig
} from "../types/venues";
import { WebsocketBookingUpdated } from "../types/websocketBookingUpdated";
import {
  addOrUpdateBooking,
  mapBookingToV4,
  mapBookingV4ToState
} from "./../APIs/Booking/UpdateBooking";
import {
  autoConfirmedFilter,
  collinsCallChanneFilter,
  customerRequestsFilter,
  doubleConfirmedFilter,
  notDoubleConfirmedFilter,
  runSheetNotesFilter
} from "./bookingFilters";
import { CompareProps, findSortedIndex, getDefinedProps } from "./helpers";
import { getWaitlistTimeString } from "./singleBooking";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/**
 * Determines if a booking is on the same day
 * @param date1
 * @param date2
 * @returns
 */
export const isSameCollinsDay = (date1: Date, date2: Date): boolean =>
  dayjs(date1).isSame(dayjs(date2), "day");

/**
 * Formats a time from 24 hour format to the locale format (e.g. 14:00 -> 2:00 PM)
 * @param time
 * @returns formattedTime
 */
export const formatTimeForLocale = (time: string, locale?: string): string => {
  if (!time) {
    return "";
  }

  const [hours, mins] = time.split(":");

  if (!hours || !mins) {
    return "";
  }

  const bookingTime = getTimeFromString(time);
  const dateTime = dayjs(bookingTime);

  if (locale !== undefined && locale === "en-US") {
    return dateTime.format("h:mm A");
  } else {
    return dateTime.format("HH:mm");
  }
};

/**
 * Returns the appropriate Day.js formatting options string based on user locale.
 * Used for MobileTimeInput components to display times in the correct format.
 * @param locale - The user's locale string (e.g., "en-US")
 * @returns Day.js format string: "h:mm a" for US locale (12-hour), "HH:mm" for others (24-hour)
 */
export const getFormattingOptions = (locale?: string): string => {
  return locale === "en-US" ? "h:mm a" : "HH:mm";
};

/**
 * Converts a string to a date (sets the day to today)
 * @param time
 * @returns
 */
export const getTimeFromString = (time: string): Date => {
  // Handle empty or invalid time strings
  if (!time || typeof time !== "string" || !time.includes(":")) {
    // Return current time as fallback
    return new Date();
  }

  const [hours, mins] = time.split(":");
  const parsedHours = parseInt(hours);
  const parsedMins = parseInt(mins);

  // Validate parsed values
  if (isNaN(parsedHours) || isNaN(parsedMins)) {
    // Return current time as fallback
    return new Date();
  }

  const parsedTime = dayjs().hour(parsedHours).minute(parsedMins).second(0);

  // Handle invalid dayjs objects
  if (!parsedTime.isValid()) {
    return new Date();
  }

  return parsedTime.toDate();
};

/**
 * Join firstname and lastname
 * @param firstName
 * @param lastName
 * @returns fullName
 */
export const getCustomerName = (booking: ViewBooking): string => {
  let name = "No Name";

  if (booking.walkIn) {
    name = "Walk In";
  }
  if (!!booking.firstName || !!booking.lastName) {
    name = `${booking?.firstName ?? ""} ${booking?.lastName ?? ""}`.trim();
  }

  return name;
};

/**
 * Filters bookings given a filter function (@see booking-filters)
 * @param bookings
 * @param filterFunction
 * @returns bookings
 */
export const filterBookings = (
  bookings: ViewBooking[] | undefined,
  filterFunction: any
): ViewBooking[] => bookings?.filter(filterFunction) ?? [];

export const bookingZone = (
  theAssignedAreas: Array<any>,
  bookingZoneFilters: Array<string>
): boolean => {
  for (let i = 0; i < theAssignedAreas.length; i++) {
    if (bookingZoneFilters.indexOf(theAssignedAreas[i].zone) > -1) {
      return true;
    }
  }

  return false;
};

export const bookingTimeSection = (
  time: string,
  timeOfTheDayFilters: Array<string>
): boolean => {
  if (!time) {
    return false;
  }

  let theTimeString = "";
  const theHour = parseInt(time.split(":")[0]);

  if (theHour >= 0 && theHour < 12) {
    theTimeString = "morning";
  } else if (theHour >= 12 && theHour < 16) {
    theTimeString = "afternoon";
  } else if (theHour >= 16) {
    theTimeString = "evening";
  }

  return timeOfTheDayFilters.indexOf(theTimeString) > -1;
};

export const bookingAttributesListFilter = (
  booking: ViewBooking,
  bookingAttributesFilters: Array<string>
) => {
  return (
    bookingAttributesFilters.find((currentBookingAttributeFilter) => {
      switch (currentBookingAttributeFilter) {
        case "autoConfirmed":
          return autoConfirmedFilter(booking);

        case "customerRequests":
          return customerRequestsFilter(booking);

        case "doubleConfirmed":
          return doubleConfirmedFilter(booking);

        case "runSheetNotes":
          return runSheetNotesFilter(booking);
        case "notDoubleConfirmed":
          return notDoubleConfirmedFilter(booking);

        case "collinsCall":
          return collinsCallChanneFilter(booking);
      }
    }) != undefined
  );
};

export const getBookingList = (
  bookings: ViewBooking[] | undefined,
  timeFilters: Array<string>,
  zoneFilter: Array<string>,
  attributeFilter: Array<string>,
  typeFilter: Array<string>
): ViewBooking[] => {
  let sortedBookings: Array<ViewBooking> = [];

  if (!bookings || bookings.length === 0) {
    return [];
  }

  if (
    timeFilters.length === 0 &&
    zoneFilter.length === 0 &&
    attributeFilter.length === 0 &&
    typeFilter.length === 0
  ) {
    return bookings;
  }

  if (timeFilters.length > 0) {
    sortedBookings = timeSectionFilter(bookings, timeFilters, sortedBookings);
  }

  if (typeFilter.length > 0) {
    sortedBookings = bookingTypeFilter(bookings, typeFilter, sortedBookings);
  }
  if (zoneFilter.length > 0) {
    sortedBookings = bookingZoneFilter(bookings, zoneFilter, sortedBookings);
  }

  if (attributeFilter.length > 0) {
    sortedBookings = bookingAttributeFilter(
      bookings,
      attributeFilter,
      sortedBookings
    );
  }

  return sortedBookings;
};

/**
 * Merge new bookings in to an existing set of bookings
 *
 * @param existingBookings
 * @param bookingsToMerge
 * @returns
 */
export const mergeBookings = (
  existingBookings: ViewBooking[] | undefined,
  bookingsToMerge: ViewBooking[] | undefined
): ViewBooking[] => {
  const updatedBookings: ViewBooking[] =
    existingBookings?.map((existingBooking) => {
      const bookingIndex = bookingsToMerge?.findIndex(
        ({ id }) => id === existingBooking.id
      );
      return bookingsToMerge && bookingIndex && bookingIndex > -1
        ? bookingsToMerge?.splice(bookingIndex, 1)[0]
        : (existingBooking ?? []);
    }) ?? [];

  return updatedBookings.concat(bookingsToMerge ?? []);
};

/**
 * Search for a booking using a term accorss fields
 * 'first_name', 'last_name', 'email', 'phone', 'reference'
 *
 * It also caters for "first_name last_name" e.g. "Alasdair Mackenzie"
 *
 * It will also return walk in bookings for the term 'walk in'
 *
 * @param term
 * @param bookings
 * @returns bookings
 */
export const filterBookingsForTerm = (
  bookings: ViewBooking[] | undefined,
  term: string
): ViewBooking[] => {
  if (!bookings) {
    return [];
  }

  if (term === "") {
    return bookings;
  }

  if ("walk in".startsWith(term.toLowerCase())) {
    return bookings.filter(
      (booking: any) => booking["walkIn"] || booking["walk_in"]
    );
  }

  return bookings.filter((booking: any) => {
    return [
      `${booking["first_name"]} ${booking["last_name"]}`,
      `${booking["first_name"]}`,
      `${booking["last_name"]}`,
      `${booking["firstName"]} ${booking["lastName"]}`,
      `${booking["firstName"]}`,
      `${booking["lastName"]}`,
      `${booking["email"]}`,
      `${booking["phone"]}`,
      `${booking["reference"]}`
    ].find((fieldValue: string) => {
      return fieldValue.toLowerCase().includes(term.toLowerCase());
    });
  });
};

/**
 * Get Booking Duration
 * @param startTime
 * @param endTime
 * @returns
 */
export const getBookingDuration = (startTime?: string, endTime?: string) => {
  if (!startTime || !endTime) {
    return 0;
  }

  const startDateTime = new Date(`${new Date().toDateString()} ${startTime}`);
  const endDateTime = new Date(`${new Date().toDateString()} ${endTime}`);

  let diff = (endDateTime.getTime() - startDateTime.getTime()) / 1000;
  diff /= 60;

  if (diff < 0) {
    diff = 0;
  }

  return Math.abs(Math.round(diff));
};

export const leftBookingStage: BookingStage = {
  id: BookingStagesEnum.Left,
  colour: "666666",
  label: "Left",
  walk_in_default: false
};

type GetBookingStagesProps = {
  venueConfig: VenueConfig;
  venueGroupConfig: VenueGroupConfig;
};
export const getBookingStages = ({
  venueConfig,
  venueGroupConfig
}: GetBookingStagesProps) => {
  let stages = venueGroupConfig.booking_stages || [];
  if (venueConfig?.override_booking_stages) {
    stages = venueConfig.booking_stages || [];
  }

  return stages.concat([leftBookingStage]);
};

// Reusable filter functions for runsheet filtering
export const filterActiveBookings = (
  bookings: ViewBooking[]
): ViewBooking[] => {
  // Filter out bookings that should never appear on runsheet
  // Left bookings are intentionally excluded - runsheet only shows active bookings
  return bookings.filter(
    (b) =>
      b.status !== "rejected" &&
      b.currentStage !== "no-show" &&
      b.currentStage?.toLowerCase() !== "left"
  );
};

export const filterAwaitingArrivalBookings = (
  bookings: ViewBooking[]
): ViewBooking[] => {
  // Show bookings that are still awaiting arrival (before check-in)
  // This includes bookings with no stage or in "awaiting" stage
  // "Arrived" is the first status AFTER check-in, so it should NOT be included
  return bookings.filter(
    (b) => !b.currentStage || b.currentStage === "awaiting"
  );
};

export const filterCheckedInBookings = (
  bookings: ViewBooking[]
): ViewBooking[] => {
  // Show bookings that have checked in (any stage except awaiting/left/cancelled/rejected)
  // This includes "Arrived", "Dessert", "Bill", etc. - all post-check-in stages
  const excludedStages = ["awaiting", "cancelled", "rejected", "no-show"];

  return bookings.filter(
    (b) =>
      b.currentStage && !excludedStages.includes(b.currentStage.toLowerCase())
  );
};

export const filterBookingsByStage = (
  bookings: ViewBooking[],
  stageId: string
): ViewBooking[] => {
  // Filter bookings by specific stage ID
  return bookings.filter((b) => b.currentStage === stageId);
};

export const getExcludedStages = (): string[] => {
  // Define excluded stages that should not appear in filter dropdown
  return ["left", "cancelled", "rejected", "no-show"];
};

export const getNextBookingStageFromKey = (
  key: string,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): BookingStage | null => {
  const stages = getBookingStages({
    venueConfig,
    venueGroupConfig
  });

  const indexOfCurrentStage = stages.findIndex(
    (stage: BookingStage) => stage.id === key
  );

  return stages[indexOfCurrentStage + 1] || stages[0];
};

export const walkingBookings = (
  key: string,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): BookingStage | null => {
  const stages = getBookingStages({
    venueConfig,
    venueGroupConfig
  });

  return stages.find((s) => s.id === key) ?? null;
};

export const getBookingStageFromKey = (
  key: string,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): BookingStage | null => {
  const stages = getBookingStages({
    venueConfig,
    venueGroupConfig
  });

  const indexOfCurrentStage = stages.findIndex(
    (stage: BookingStage) => stage.id === key
  );

  return stages[indexOfCurrentStage] || null;
};

/**
 * Get Zone Name From Zone Id
 * @param zoneId
 * @param zoneList
 * @returns zoneName
 */
export function getZoneNameFormZoneId(
  zoneId: string,
  zoneList: BookingZone[]
): string {
  let zoneName = "";
  if (zoneId && zoneList) {
    zoneList.forEach((list) => {
      if (list.id == zoneId) {
        zoneName = list.name ?? "";
      }
    });
  }

  return zoneName;
}
/**
 * Get Guest Count from booking list
 * @param bookingList
 * @returns totalNoOfGuest
 */
export function getGuestCount(bookingList: ViewBooking[]): number {
  let count = 0;

  bookingList.forEach((open) => {
    if (open.actualGuests) {
      count = count + open.actualGuests;
    }
  });

  return count;
}

/**
 * Checks if a booking has the basic information required
 * @param booking
 * @returns boolean
 */
export function isBookingValid(booking: ViewBooking) {
  return (
    !booking.date ||
    !booking.numPeople ||
    booking.numPeople === 0 ||
    !booking.type ||
    !booking.time ||
    !booking.duration ||
    booking.duration === 0
  );
}

export function isNonWalkInBookingValid(booking: ViewBooking) {
  return (
    !booking.firstName ||
    !booking.date ||
    !booking.numPeople ||
    booking.numPeople === 0 ||
    !booking.type ||
    (!booking.time && !booking.waitlisted) ||
    !booking.duration ||
    booking.duration === 0
  );
}

export function getAvailableAreaForBooking(
  booking: ViewBooking,
  bookableAreaList: BookableArea[],
  selectedType: BookingType | undefined
): BookableArea[] {
  const sortedTableList: BookableArea[] = [];
  if (!bookableAreaList || !booking.numPeople) {
    return sortedTableList;
  }
  const numOfPeopleCount = booking.numPeople;
  bookableAreaList.forEach((bookableArea) => {
    if (
      (bookableArea?.min_people ?? 0) <= numOfPeopleCount &&
      (bookableArea?.max_people ?? 0) >= numOfPeopleCount
    ) {
      if (selectedType?.areas) {
        selectedType.areas.forEach((selectedTypeArea: Areas) => {
          if (selectedTypeArea.id === bookableArea.id) {
            sortedTableList.push(bookableArea);
          }
        });
      } else {
        sortedTableList.push(bookableArea);
      }
    }
  });

  return sortedTableList;
}

/**
 * Get the end time for a booking, handling "until close" bookings (duration -1)
 * @param booking - The booking object
 * @param venueConfig - Optional venue configuration for opening times
 * @returns End time in HH:mm format
 */
export function getEndTime(
  booking: ViewBooking,
  venueConfig?: VenueConfig
): string {
  if (!booking?.date) {
    return "";
  }

  // Handle "until close" bookings (duration -1)
  if (booking.duration === -1) {
    if (venueConfig?.opening_times) {
      const bookingDate = new Date(booking.date);
      const { endMinutes } = getOpenCloseTimeInMinutes({
        date: bookingDate,
        openingTimes: venueConfig.opening_times
      });

      // Convert minutes to HH:mm format
      const hours = Math.floor(endMinutes / 60) % 24;
      const minutes = endMinutes % 60;
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    } else {
      // Fallback: if no venue config, return the start time
      return booking.time ?? "";
    }
  }

  const date = new Date(booking.date ?? "");
  const fromTime = new Date(`${date?.toDateString()} ${booking.time}`);
  const untilTime = booking.time
    ? dayjs(
        new Date(fromTime.getTime() + (booking.duration || 0) * 60000)
      ).format("HH:mm")
    : "";

  return untilTime;
}

/**
 * Calculate min/max time constraints from a booking's start time and duration.
 * Useful for constraining time pickers to the booking's time window.
 * @param booking - Object containing time (HH:mm format) and duration (minutes)
 * @returns Object with minTime (booking start) and maxTime (booking end) in HH:mm format
 */
/**
 * Checks if a string value contains valid note/description content.
 * Returns false for empty strings, whitespace-only strings, or the literal string "false"
 * which can appear in legacy data.
 * @param value - The string value to check
 * @returns true if the value contains valid content, false otherwise
 */
export const isValidNoteContent = (
  value: string | undefined | null
): boolean => {
  if (!value) return false;
  if (value === "false") return false;
  if (value.trim() === "") return false;
  return true;
};

export const getBookingTimeConstraints = (
  booking: { time?: string; duration?: number } | null | undefined
): { minTime: string | undefined; maxTime: string | undefined } => {
  if (!booking?.time || !booking?.duration) {
    return { minTime: undefined, maxTime: undefined };
  }

  const startTime = booking.time;
  const durationMinutes = booking.duration;

  // Parse start time (format: "HH:mm")
  const [startHours, startMinutes] = startTime.split(":").map(Number);
  if (isNaN(startHours) || isNaN(startMinutes)) {
    return { minTime: undefined, maxTime: undefined };
  }

  // Calculate end time
  const totalMinutes = startHours * 60 + startMinutes + durationMinutes;
  const endHours = Math.floor(totalMinutes / 60) % 24;
  const endMinutes = totalMinutes % 60;

  const minTime = `${startHours.toString().padStart(2, "0")}:${startMinutes.toString().padStart(2, "0")}`;
  const maxTime = `${endHours.toString().padStart(2, "0")}:${endMinutes.toString().padStart(2, "0")}`;

  return { minTime, maxTime };
};

export const bookingTypeFilter = (
  bookings: ViewBooking[],
  bookingTypeFilters: Array<string>,
  sortedBookingList: ViewBooking[]
) => {
  return sortedBookingList.length > 0
    ? sortedBookingList.filter((booking) => {
        if (booking.type?.id) {
          return bookingTypeFilters.indexOf(booking.type.id) > -1;
        }
      })
    : bookings.filter((booking) => {
        if (booking.type?.id) {
          return bookingTypeFilters.indexOf(booking.type.id) > -1;
        }
      });
};

export const bookingZoneFilter = (
  bookings: ViewBooking[],
  bookingZoneFilters: Array<string>,
  sortedBookingList: ViewBooking[]
) => {
  return sortedBookingList.length > 0
    ? sortedBookingList.filter((booking) => {
        if (booking.assignedAreas) {
          return bookingZone(booking.assignedAreas, bookingZoneFilters);
        }
      })
    : bookings.filter((booking) => {
        if (booking.assignedAreas) {
          return bookingZone(booking.assignedAreas, bookingZoneFilters);
        }
      });
};

export const timeSectionFilter = (
  bookings: ViewBooking[],
  timeOfTheDayFilters: Array<string>,
  sortedBookingList: ViewBooking[]
) => {
  return sortedBookingList.length > 0
    ? sortedBookingList.filter((booking) => {
        return bookingTimeSection(booking.time ?? "", timeOfTheDayFilters);
      })
    : bookings.filter((booking) => {
        return bookingTimeSection(booking.time ?? "", timeOfTheDayFilters);
      });
};

export const bookingAttributeFilter = (
  bookings: ViewBooking[],
  timeOfTheDayFilters: Array<string>,
  sortedBookingList: ViewBooking[]
) => {
  return sortedBookingList.length > 0
    ? sortedBookingList.filter((booking) => {
        if (booking) {
          return bookingAttributesListFilter(booking, timeOfTheDayFilters);
        }
      })
    : bookings.filter((booking) => {
        if (booking) {
          return bookingAttributesListFilter(booking, timeOfTheDayFilters);
        }
      });
};

export const getZoneWiseAllArea = (
  bookingZoneId: string,
  bookingArea: Array<BookableArea>
) => {
  if (!bookingArea) {
    return [];
  }
  const areaList: BookableArea[] = [];
  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      areaList.push(bookingArea);
    }
  });

  return areaList;
};

export const getZoneWiseAllAreaCombine = (
  bookingZoneId: string,
  bookingArea: Array<BookableArea>,
  bookingAreaCombination: Array<BookableAreaCombination>
) => {
  if (!bookingAreaCombination) {
    return [];
  }
  const areaList: BookableAreaCombination[] = [];
  bookingAreaCombination.forEach(
    (bookingAreaCombination: BookableAreaCombination) => {
      bookingAreaCombination.area_ids.forEach((id: string) => {
        const bookableAreas = getBookableAreaFromAreaId(id, bookingArea);
        bookingArea.forEach(() => {
          if (bookingZoneId === bookableAreas.zone) {
            areaList.push(bookingAreaCombination);
          }
        });
      });
    }
  );
  const areaListCombine = areaList.filter(function (val, id, array) {
    return array.indexOf(val) === id;
  });
  return areaListCombine;
};

export const getNoZoneWiseAllAreaCombine = (
  bookingZoneId: string,
  bookingArea: Array<BookableArea>,
  bookingAreaCombination: Array<BookableAreaCombination>
) => {
  if (!bookingAreaCombination) {
    return [];
  }
  const areaList: BookableAreaCombination[] = [];
  bookingAreaCombination.forEach(
    (bookingAreaCombination: BookableAreaCombination) => {
      bookingAreaCombination.area_ids.forEach((id: string) => {
        const bookableAreas = getBookableAreaFromAreaId(id, bookingArea);
        bookingArea.forEach(() => {
          if (bookableAreas.zone === undefined) {
            areaList.push(bookingAreaCombination);
          }
        });
      });
    }
  );
  const areaListCombine = areaList.filter(function (val, id, array) {
    return array.indexOf(val) === id;
  });
  return areaListCombine;
};

export const getZoneWiseFreeArea = (
  bookingZoneId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  bookingTime: string,
  bookingEndTime: string
) => {
  if (!bookingArea) {
    return [];
  }
  const areaList: BookableArea[] = [];
  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      if (
        !checkAreaIsOccupiedOrNot(
          bookingArea.id,
          bookingList,
          bookingTime,
          bookingEndTime
        )
      ) {
        areaList.push(bookingArea);
      }
    }
  });

  return areaList;
};

export const getNoZoneWiseFreeArea = (
  areaId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  bookingTime: string,
  bookingEndTime: string
) => {
  if (!bookingArea) {
    return [];
  }
  const areaList: BookableArea[] = [];
  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingArea.zone === undefined) {
      if (
        !checkAreaIsOccupiedOrNot(
          bookingArea.id,
          bookingList,
          bookingTime,
          bookingEndTime
        )
      ) {
        areaList.push(bookingArea);
      }
    }
  });

  return areaList;
};

export const getZoneWiseFreeAreaCombine = (
  bookingZoneId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  bookingAreaCombination: Array<BookableAreaCombination>,
  bookingTime: string,
  bookingEndTime: string
) => {
  if (!bookingAreaCombination) {
    return [];
  }
  const areaList: BookableAreaCombination[] = [];
  bookingAreaCombination.forEach(
    (bookingAreaCombination: BookableAreaCombination) => {
      bookingAreaCombination.area_ids.forEach((id: string) => {
        const bookableAreas = getBookableAreaFromAreaId(id, bookingArea);

        if (bookingZoneId === bookableAreas.zone) {
          if (
            !checkAreaIsOccupiedOrNot(
              bookableAreas.id,
              bookingList,
              bookingTime,
              bookingEndTime
            )
          ) {
            areaList.push(bookingAreaCombination);
          }
        }
      });
    }
  );
  const areaListCombine = areaList.filter(function (val, id, array) {
    return array.indexOf(val) === id;
  });
  return areaListCombine;
};

export const getNoZoneWiseFreeAreaCombine = (
  bookingZoneId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  bookingAreaCombination: Array<BookableAreaCombination>,
  bookingTime: string,
  bookingEndTime: string
) => {
  if (!bookingAreaCombination) {
    return [];
  }
  const areaList: BookableAreaCombination[] = [];
  bookingAreaCombination.forEach(
    (bookingAreaCombination: BookableAreaCombination) => {
      bookingAreaCombination.area_ids.forEach((id: string) => {
        const bookableAreas = getBookableAreaFromAreaId(id, bookingArea);

        if (bookableAreas.zone === undefined) {
          if (
            !checkAreaIsOccupiedOrNot(
              bookableAreas.id,
              bookingList,
              bookingTime,
              bookingEndTime
            )
          ) {
            areaList.push(bookingAreaCombination);
          }
        }
      });
    }
  );
  const areaListCombine = areaList.filter(function (val, id, array) {
    return array.indexOf(val) === id;
  });
  return areaListCombine;
};

export const checkAreaIsOccupiedOrNot = (
  bookingAreaId: string,
  bookingList: ViewBooking[],
  bookingTime: string,
  bookingEndTime: string
) => {
  let status = false;

  bookingList.forEach((booking: ViewBooking) => {
    if (
      booking.assignedAreas &&
      booking.assignedAreas.length &&
      (booking.status === ViewBookingStatusEnum.Complete ||
        ViewBookingStatusEnum.InProgress) &&
      booking.currentStage !== "left" &&
      bookingEndTime > (booking.time || "") &&
      getEndTime(booking) > bookingTime
    ) {
      booking.assignedAreas.forEach((area: ViewBookingAssignedAreasInner) => {
        if (area.id?.toString() === bookingAreaId) {
          status = true;
        }
      });
      if (status) {
        return status;
      }
    }
  });

  return status;
};

export const occupiedAreaEndTime = (
  bookingAreaId: string,
  bookingList: ViewBooking[]
) => {
  let untilTime = "";
  bookingList.forEach((booking: ViewBooking) => {
    if (
      booking.assignedAreas &&
      booking.assignedAreas.length &&
      (booking.status === ViewBookingStatusEnum.Complete ||
        ViewBookingStatusEnum.InProgress) &&
      booking.currentStage !== "left"
    ) {
      booking.assignedAreas.forEach((area: ViewBookingAssignedAreasInner) => {
        if (area.id?.toString() === bookingAreaId) {
          untilTime = getEndTime(booking);
        }
      });
      if (untilTime) {
        return untilTime;
      }
    }
  });

  return untilTime;
};

export const getBookableAreaFromAreaId = (
  bookingAreaId: string,
  bookingArea: Array<BookableArea>
): BookableArea => {
  let bookableArea = {} as BookableArea;
  if (!bookingArea) {
    return bookableArea;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingAreaId === bookingArea.id) {
      bookableArea = bookingArea;
    }
  });

  return bookableArea;
};

export const getBookableAreaListFromAreaId = (
  bookingAreaId: string,
  bookingArea: Array<BookableArea>
): Array<BookableArea> => {
  const bookableAreaList: BookableArea[] = [];
  if (!bookingArea) {
    return bookableAreaList;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingAreaId === bookingArea.id) {
      bookableAreaList.push(bookingArea);
    }
  });

  return bookableAreaList;
};

export const getAvailableCombinationAreaForBooking = (
  booking: ViewBooking,
  bookableAreaCombination: BookableAreaCombination[],
  bookings: ViewBooking[] | undefined
): BookableAreaCombination[] => {
  const sortedCombinedList: BookableAreaCombination[] = [];
  if (!bookableAreaCombination || !booking.numPeople || !bookings) {
    return sortedCombinedList;
  }
  const numberOfPeople = booking.numPeople;
  bookableAreaCombination.forEach(
    (areaCombination: BookableAreaCombination) => {
      if (
        areaCombination.max_people <= numberOfPeople &&
        areaCombination.max_people_seated &&
        areaCombination.max_people_seated >= numberOfPeople &&
        !checkJoinAreaIsOccupiedOrNot(
          areaCombination.area_ids,
          bookings,
          booking.time || "",
          getEndTime(booking)
        )
      ) {
        sortedCombinedList.push(areaCombination);
      }
    }
  );

  return sortedCombinedList;
};
export const getBookableAreaNameFromAreaId = (
  bookableAreaId: string,
  bookableArea: Array<BookableArea>
): string => {
  let areaName = "";
  if (!bookableArea) {
    return areaName;
  }
  bookableArea.forEach((bookingArea: BookableArea) => {
    if (bookableAreaId === bookingArea.id) {
      areaName = bookingArea.name || "";
    }
  });

  return areaName;
};

export const getAllAreaZoneWiseCount = (
  bookingZoneId: string,
  bookingArea: Array<BookableArea>
) => {
  let count = 0;
  if (!bookingArea) {
    return count;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      count = count + 1;
    }
  });

  return count;
};

export const getAllAreaNoZoneWiseCount = (bookingArea: Array<BookableArea>) => {
  let count = 0;
  if (!bookingArea) {
    return count;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingArea.zone === undefined) {
      count = count + 1;
    }
  });

  return count;
};

export const getFreeAreaZoneWiseCount = (
  bookingZoneId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  combinationAreaCount: number,
  bookingTime: string,
  bookingEndTime: string,
  bookingType?: BookingType
) => {
  let count = 0;
  if (!bookingArea) {
    return count;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      if (
        !checkAreaIsOccupiedOrNot(
          bookingArea.id,
          bookingList,
          bookingTime,
          bookingEndTime
        )
      ) {
        count = count + 1;
      }
    }
  });

  const areaList: BookableArea[] = [];
  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      if (bookingType?.areas) {
        bookingType?.areas.forEach((selectedTypeArea: Areas) => {
          if (selectedTypeArea.id === bookingArea.id) {
            areaList.push(bookingArea);
          }
        });
      } else {
        areaList.push(bookingArea);
      }
    }
  });

  return count;
};

export const getFreeAreaNoZoneWiseCount = (
  bookingZoneId: string,
  bookingList: ViewBooking[],
  bookingArea: Array<BookableArea>,
  combinationAreaCount: number,
  bookingTime: string,
  bookingEndTime: string,
  bookingType?: BookingType
) => {
  let count = 0;
  if (!bookingArea) {
    return count;
  }

  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingArea.zone === undefined) {
      if (
        !checkAreaIsOccupiedOrNot(
          bookingArea.id,
          bookingList,
          bookingTime,
          bookingEndTime
        )
      ) {
        count = count + 1;
      }
    }
  });

  const areaList: BookableArea[] = [];
  bookingArea.forEach((bookingArea: BookableArea) => {
    if (bookingZoneId === bookingArea.zone) {
      if (bookingType?.areas) {
        bookingType?.areas.forEach((selectedTypeArea: Areas) => {
          if (selectedTypeArea.id === bookingArea.id) {
            areaList.push(bookingArea);
          }
        });
      } else {
        areaList.push(bookingArea);
      }
    }
  });

  return count;
};

export const getBookableAreaFromJoinTableId = (
  bookableArea: Array<BookableArea>,
  bookableAreaCombinationList?: BookableAreaCombination
): Array<BookableArea> => {
  const bookableAreaList: BookableArea[] = [];
  if (!bookableAreaCombinationList) {
    return bookableAreaList;
  }

  bookableAreaCombinationList.area_ids.forEach((id: string) => {
    const bookableAreas = getBookableAreaFromAreaId(id, bookableArea);
    if (bookableAreas.id) {
      bookableAreaList.push(bookableAreas);
    }
  });

  return bookableAreaList;
};

export const getAlreadyJoinAssignedAreaList = (
  bookableAreas: Array<BookableArea>,
  diaryModalBooking?: ViewBooking,
  bookingList?: ViewBooking[]
) => {
  const bookedAreaList: ViewBooking[] = [];

  // Check we have all the data we need to continue
  if (
    !bookableAreas ||
    !bookingList ||
    !diaryModalBooking ||
    !diaryModalBooking.date ||
    !diaryModalBooking.time ||
    !diaryModalBooking.duration ||
    !diaryModalBooking.assignedAreas ||
    diaryModalBooking.assignedAreas.length === 0
  ) {
    return [];
  }

  // set new booking start and end date ready for compare
  const newBookingFromTime = diaryModalBooking.time.split(":");
  const newBookingFrom = new Date(diaryModalBooking.date);
  newBookingFrom.setHours(
    parseInt(`${newBookingFromTime?.[0] ?? 0}`),
    parseInt(`${newBookingFromTime?.[1] ?? 0}`),
    0
  );
  const newBookingTo = new Date(
    newBookingFrom.getTime() + diaryModalBooking.duration * 60000
  );

  // reduce new booking area ids ready for compare
  const newBookingAreaIds = diaryModalBooking.assignedAreas.reduce(
    (ids, area) => {
      if (area.id) {
        ids.push(area.id.toString());
      }
      return ids;
    },
    [] as string[]
  );

  bookingList.forEach((booking: ViewBooking) => {
    // Check existing bookings aren't cancelled, already left or marked as a no show
    if (
      booking.status === ViewBookingStatusEnum.Rejected ||
      booking.status === ViewBookingStatusEnum.Deleted ||
      booking.status === ViewBookingStatusEnum.Lost ||
      booking.currentStage === "left" ||
      booking.actualGuests === 0
    ) {
      return [];
    }

    // Check existing bookings have all the data we need to compare
    if (
      !booking.date ||
      !booking.time ||
      !booking.duration ||
      !booking.assignedAreas
    ) {
      return [];
    }

    // Check if existing bookings are in the same area/table
    const matchedAreas = booking.assignedAreas.filter(
      (area) => area.id && newBookingAreaIds.includes(String(area.id))
    );
    if (matchedAreas.length === 0) {
      return [];
    }

    // Check new booking time + duration doesn't overlap existing bookings
    const bookingFromTime = booking.time.split(":");
    const bookingFrom = new Date(booking.date);
    bookingFrom.setHours(
      parseInt(`${bookingFromTime?.[0] ?? 0}`),
      parseInt(`${bookingFromTime?.[1] ?? 0}`),
      0
    );
    const bookingTo = new Date(
      bookingFrom.getTime() + booking.duration * 60000
    );

    const bookingTimesOverlap =
      bookingFrom <= newBookingTo && bookingTo >= newBookingFrom;
    if (!bookingTimesOverlap) {
      return [];
    }

    bookedAreaList.push(booking);
  });

  return bookedAreaList;
};

export const checkJoinAreaIsOccupiedOrNot = (
  joinAreasId: string[],
  bookingList: ViewBooking[],
  bookingTime: string,
  bookingEndTime: string
) => {
  let status = false;
  bookingList.forEach((booking: ViewBooking) => {
    if (
      booking.assignedAreas &&
      booking.assignedAreas.length &&
      (booking.status === ViewBookingStatusEnum.Complete ||
        ViewBookingStatusEnum.InProgress) &&
      booking.currentStage !== "left" &&
      bookingEndTime > (booking.time || "") &&
      getEndTime(booking) > bookingTime &&
      joinAreasId
    ) {
      let count = 0;
      booking.assignedAreas.forEach(
        (area: ViewBookingAssignedAreasInner, index: number) => {
          if (area.id?.toString() === joinAreasId[index]) {
            count = count + 1;
            if (
              joinAreasId.length === booking.assignedAreas?.length &&
              count === booking.assignedAreas?.length
            ) {
              status = true;
            }
          }
        }
      );
      if (status) {
        return status;
      }
    }
  });

  return status;
};

export function isToTimeValid(booking: ViewBooking) {
  return (
    !booking.date ||
    !booking.numPeople ||
    booking.numPeople === 0 ||
    !booking.time
  );
}

type GetOpenCloseTimeInMinutesType = {
  date: Date;
  openingTimes?: OpeningTimes;
};
type GetOpenCloseTimeInMinutesReturnType = {
  startMinutes: number;
  endMinutes: number;
};
export const getOpenCloseTimeInMinutes = ({
  date,
  openingTimes
}: GetOpenCloseTimeInMinutesType): GetOpenCloseTimeInMinutesReturnType => {
  let startMinutes = 540;
  let endMinutes = 1320;

  if (!openingTimes) {
    return { startMinutes, endMinutes };
  }

  const openingTime: OpeningTime[] = [
    openingTimes.sunday,
    openingTimes.monday,
    openingTimes.tuesday,
    openingTimes.wednesday,
    openingTimes.thursday,
    openingTimes.friday,
    openingTimes.saturday
  ];

  const openingTimeSelected = openingTime[date.getDay()];

  if (!openingTimeSelected) {
    return { startMinutes, endMinutes };
  }

  const startTime = openingTimeSelected.open
    ? openingTimeSelected.open.split(":")
    : ["12", "00"];
  const endTime = openingTimeSelected.close
    ? openingTimeSelected.close.split(":")
    : ["23", "00"];

  const startTimeMinutes = Number(startTime[1] ?? 0) / 60;
  const endTimeMinutes = Number(endTime[1] ?? 0) / 60;

  const startHour = Number(startTime[0]) + startTimeMinutes;
  let endHour = Number(endTime[0]) + endTimeMinutes;

  if (startHour > endHour) {
    endHour += 24;
  }

  startMinutes = startHour * 60;
  endMinutes = endHour * 60;

  return { startMinutes, endMinutes };
};

export const getAreaWithoutZone = (
  venueConfig?: VenueConfig
): BookableArea[] => {
  const areas: BookableArea[] = [];
  if (venueConfig?.bookable_areas) {
    venueConfig.bookable_areas.forEach((bookableAreaList: BookableArea) => {
      if (bookableAreaList.zone === undefined) {
        areas.push(bookableAreaList);
      }
    });
  }
  return areas;
};

export const getBookingsForGrid = (bookings?: ViewBooking[]): Booking[] =>
  (bookings?.filter(
    (bookingFilter) => bookingFilter.status !== ViewBookingStatusEnum.Rejected
  ) ?? []) as Booking[];

export const mapWebsocketBookingToState = (
  websocketBooking: WebsocketBookingUpdated
): ViewBooking & Partial<BookingV4> => ({
  ...websocketBooking,
  id: websocketBooking._id,
  assignedTo: websocketBooking.assigned_to,
  createdBy: websocketBooking.created_by,
  status: websocketBooking.status as ViewBookingStatusEnum,
  venueId: websocketBooking.venue_id,
  venueGroup: websocketBooking.venue_group,
  assignedAreas:
    websocketBooking.assigned_areas as unknown as ViewBookingAssignedAreasInner[],
  numPeople: websocketBooking.num_people,
  date: websocketBooking.date,
  time: websocketBooking.time,
  duration: websocketBooking.duration,
  type: {
    ...(websocketBooking.type ?? {}),
    privateHire: websocketBooking.type?.private_hire
  },
  value: websocketBooking.value,
  currentStage: websocketBooking.current_stage,
  walkIn: websocketBooking.walk_in,
  createdDate: websocketBooking.created_date,
  lastUpdated: websocketBooking.last_updated
});

export const updateStateBooking = (
  updatedBooking: ViewBooking,
  diaryContext: DiaryContextType
) => {
  let selectedBooking = diaryContext.selectedBooking;
  let selectedBookingV4 = diaryContext.selectedBookingV4;

  const updatedBookingV4 = getDefinedProps(
    mapBookingToV4({ ...updatedBooking })
  );
  const updateBooking = getDefinedProps(
    mapBookingV4ToState({ ...updatedBookingV4 })
  );

  if (updatedBooking.currentStage === undefined) {
    updateBooking.currentStage = updatedBookingV4.current_stage = "";
  }

  if (selectedBookingV4?._id === updatedBookingV4?._id) {
    selectedBookingV4 = {
      ...selectedBookingV4,
      ...updatedBookingV4
    } as BookingV4;
  }

  if (updatedBooking.id === selectedBooking?.id && selectedBookingV4) {
    selectedBooking = {
      ...selectedBooking,
      ...updateBooking
    };
  }
  let existingBooking = false;

  const bookings =
    diaryContext.bookings?.map((booking) => {
      if (booking.id === updatedBooking.id) {
        existingBooking = true;
        return { ...booking, ...updateBooking };
      }
      return booking;
    }) ?? [];
  if (!existingBooking) {
    if (!updatedBooking.date) {
      return;
    }

    // Only add new bookings to the list booking date matches the selected date
    const diaryDate = new Date(diaryContext.date);
    const updatedDate = new Date(updatedBooking.date);

    const isSameDay =
      diaryDate.getFullYear() === updatedDate.getFullYear() &&
      diaryDate.getMonth() === updatedDate.getMonth() &&
      diaryDate.getDate() === updatedDate.getDate();

    if (!isSameDay) {
      return;
    }

    // actualGuests is missing from websocket for new bookings
    // this is needed because we are treating bookings without actualGuests > 0 as "left"
    const newBooking = {
      actualGuests: updatedBooking.numPeople,
      ...updateBooking
    };
    const index = findSortedIndex({
      array: bookings ?? [],
      element: newBooking,
      compareFn
    });

    bookings.splice(index, 0, newBooking);
  }

  diaryContext.setDiaryContext({
    selectedBooking,
    bookings,
    selectedBookingV4
  });
};
export const updateFloorPlanBooking = async (
  updatedBooking: ViewBooking,
  diaryContext: DiaryContextType
) => {
  const bookings = diaryContext.bookings?.map((booking) => {
    if (booking.id === updatedBooking.id) {
      return { ...booking, ...updatedBooking };
    }
    return booking;
  });
  await diaryContext.setDiaryContext({ bookings });

  await addOrUpdateBooking({
    booking: { ...updatedBooking, additionalAssignedAreas: null }
  });
};
const compareFn = ({
  originalObject,
  newObject
}: CompareProps<ViewBooking>) => {
  let arrayTime = parseInt(`${originalObject.time ?? ""}`.split(":").join(""));
  let newTime = parseInt(`${newObject.time ?? ""}`.split(":").join(""));

  if (
    newObject.waitlisted &&
    !newObject.currentStage &&
    newObject.waitlistDetails?.availableTime
  ) {
    newTime = parseInt(
      `${getWaitlistTimeString(newObject.waitlistDetails.availableTime)}`
        .split(":")
        .join("")
    );

    if (
      originalObject.waitlisted &&
      !originalObject.currentStage &&
      originalObject.waitlistDetails?.availableTime
    ) {
      arrayTime = parseInt(
        `${getWaitlistTimeString(originalObject.waitlistDetails.availableTime)}`
          .split(":")
          .join("")
      );
    }
  }

  if (newTime < arrayTime) {
    return -1;
  }

  if (newTime > arrayTime) {
    return 1;
  }

  return 0;
};

/**
 * Checks if a current time is within a specified time range
 * @param currentTime - Current time in HH:mm format
 * @param timeFrom - Start time in HH:mm format (optional)
 * @param timeTo - End time in HH:mm format (optional)
 * @returns true if within range or no range specified, false otherwise
 */
export const isWithinTimeRange = (
  currentTime: string,
  timeFrom?: string,
  timeTo?: string
): boolean => {
  if (!timeFrom || !timeTo) {
    return true;
  }
  return currentTime >= timeFrom && currentTime <= timeTo;
};

/**
 * Checks if an area is closed today based on weekday/time restrictions or date range
 * @param closedArea - The closure configuration object
 * @param currentDay - Current day in lowercase (e.g., "monday")
 * @param currentTime - Current time in HH:mm format
 * @param currentDate - Optional Dayjs object for date range checks
 * @returns true if the area is closed today, false otherwise
 */
export const isAreaClosedToday = (
  closedArea: any,
  currentDay: string,
  currentTime: string,
  currentDate?: dayjs.Dayjs
): boolean => {
  // Handle date range closures (with date_from and date_to)
  if (closedArea.date_from && closedArea.date_to && currentDate) {
    const dateFrom = dayjs(closedArea.date_from);
    const dateTo = dayjs(closedArea.date_to);

    // Check if current date is within the closure date range
    if (
      currentDate.isSameOrAfter(dateFrom, "day") &&
      currentDate.isSameOrBefore(dateTo, "day")
    ) {
      // If there are time restrictions, check them
      if (closedArea.time_from && closedArea.time_to) {
        return isWithinTimeRange(
          currentTime,
          closedArea.time_from,
          closedArea.time_to
        );
      }
      // If no time restrictions, the area is closed all day
      return true;
    }
    return false;
  }

  // Handle weekday-based closures (existing logic)
  if (closedArea.date_type !== "weekdays" || !closedArea.weekdays) {
    return false;
  }

  const isClosedToday =
    closedArea.weekdays[currentDay as keyof typeof closedArea.weekdays];
  if (!isClosedToday) {
    return false;
  }

  return isWithinTimeRange(
    currentTime,
    closedArea.time_from,
    closedArea.time_to
  );
};
